import type Stripe from 'stripe';

export const productIdOf = (price: Stripe.Price) =>
  typeof price.product === 'string' ? price.product : price.product?.id;

export const couponApplies = (coupon: Stripe.Coupon, price: Stripe.Price) =>
  !coupon.applies_to?.products || coupon.applies_to.products.includes(productIdOf(price)!);

/**
 * Keep Settings' unrounded coupon calculation for comparisons. Preserve attached deleted coupons:
 * valid controls new redemption, not the current rate.
 */
export const applyCouponAmount = (
  cents: number,
  coupon: Stripe.Coupon | null,
  currency?: string,
) => {
  if (!coupon) return cents;
  if (typeof coupon.percent_off === 'number')
    return Math.max(0, (cents * (100 - coupon.percent_off)) / 100);
  if (typeof coupon.amount_off === 'number' && coupon.currency === currency)
    return Math.max(0, cents - coupon.amount_off);
  return cents;
};

export const discountedCents = (price: Stripe.Price, coupon: Stripe.Coupon | null) => {
  if (!Number.isSafeInteger(price.unit_amount) || price.unit_amount! < 0)
    throw new Error('A fixed offer price is required.');
  if (!coupon || !couponApplies(coupon, price)) return price.unit_amount!;
  // Preserve checkout's multiplication order: floating-point half-cent ties can round differently.
  if (typeof coupon.percent_off === 'number')
    return Math.max(0, Math.round(price.unit_amount! * (1 - coupon.percent_off / 100)));
  return Math.round(applyCouponAmount(price.unit_amount!, coupon, price.currency));
};

export const getRecurringPriceCents = (price: Stripe.Price, discount: Stripe.Discount | null) =>
  discountedCents(price, discount?.coupon.duration === 'forever' ? discount.coupon : null);

/** One decision for the first payment and renewals; only new redemptions require valid:true. */
export const selectTargetDiscount = (
  subscription: Stripe.Subscription,
  target: Stripe.Price,
  offeredCoupon: Stripe.Coupon,
  floorCents: number,
  allowFloorFallback = true,
) => {
  if (
    !offeredCoupon.valid ||
    !couponApplies(offeredCoupon, target) ||
    (typeof offeredCoupon.percent_off !== 'number' &&
      (typeof offeredCoupon.amount_off !== 'number' ||
        offeredCoupon.currency !== target.currency)) ||
    (offeredCoupon.duration === 'repeating' && offeredCoupon.duration_in_months !== 12)
  )
    throw new Error('This annual offer is not available.');
  const offeredFirst = discountedCents(target, offeredCoupon);
  const offeredRenewal = offeredCoupon.duration === 'forever' ? offeredFirst : target.unit_amount!;
  if (offeredFirst < floorCents || offeredRenewal < floorCents)
    throw new Error('This annual offer exceeds the discount limit.');
  const existing = subscription.discount?.coupon;
  const keptAmount =
    existing?.duration === 'forever' && couponApplies(existing, target)
      ? discountedCents(target, existing)
      : null;
  if (
    !allowFloorFallback &&
    keptAmount !== null &&
    keptAmount <= offeredFirst &&
    keptAmount < floorCents
  )
    throw new Error('This annual offer exceeds the discount limit.');
  // A better legacy rate follows a new term only when it is above that term's floor.
  const keepsExisting =
    keptAmount !== null && keptAmount >= floorCents && keptAmount <= offeredFirst;
  if (
    !keepsExisting &&
    keptAmount !== null &&
    keptAmount >= floorCents &&
    offeredRenewal > keptAmount
  )
    throw new Error('Your existing discount is better on renewal.');
  const coupon = keepsExisting ? existing! : offeredCoupon;
  const firstCents = discountedCents(target, coupon);
  return {
    coupon,
    keepsExisting,
    firstCents,
    renewalCents: coupon.duration === 'forever' ? firstCents : target.unit_amount!,
  };
};
